import 'project/polyfill'
import '../styles/Global.scss'
import Head from 'next/head'
import { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import { FlagsmithProvider } from 'flagsmith/react'
import { FC, useEffect, useState, useRef } from 'react'
import { createFlagsmithInstance } from 'flagsmith/isomorphic'

import { API } from 'project/api'
import { Project } from 'common/project'
import runWhenIdle from 'common/runWhenIdle'
import NProgress from 'components/util/NProgress'
import ScrollOnRoute from 'components/util/ScrollOnRoute'
import { shouldDisableTracking } from 'project/isWithoutNav'
import { NextPageWithLayout } from 'types/nextPageWithLayout'
import ToltCouponProvider from 'components/ToltCouponProvider'
import { BagItem, ShoppingBagContext } from 'common/useShoppingBag'
import { AddonSlug } from 'common/planSlugs'
import {
  CROSS_TENANT_NAVIGATION_ABORT,
  getTenantId,
  Tenant,
} from 'common/tenant'
import PageContainer from 'components/util/containers/PageContainer'
import LocaleContainer from 'components/util/containers/LocaleContainer'
import useCookieRedirect, { readCookieData } from 'project/useCookieRedirect'
import useUtmTracking from 'project/useUtmTracking'
import AnalyticsContainer from 'components/util/containers/AnalyticsContainer'

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
  pageProps: {}
}
require('public/images/plans/internation-page-plugz-head-desktop.png').default

// Eagerly initialise window.dataLayer so trackEvent pushes queue safely
// before GTM's runWhenIdle-deferred inline init runs. GTM consumes the
// queue on container load — standard gtag.js install pattern.
if (typeof window !== 'undefined') {
  ;(window as unknown as { dataLayer: unknown[] }).dataLayer =
    (window as unknown as { dataLayer: unknown[] }).dataLayer || []
}

readCookieData()

const emptyBag = (): BagItem => ({
  additionalPlans: [],
  addonSources: {} as Record<Partial<AddonSlug>, string>,
  addons: [],
  duration: 'month',
  slug: null,
})

// Load a saved basket, filling in fields older saved baskets may not have
const loadBag = (key: string): BagItem => {
  try {
    const loadedBag = JSON.parse(API.storage.getItemSync!(key))
    if (!loadedBag?.addons) {
      return emptyBag()
    }
    return {
      additionalPlans: loadedBag.additionalPlans || [],
      addonSources: loadedBag.addonSources || {},
      addons: loadedBag.addons || [],
      directors: loadedBag.directors || undefined,
      duration: loadedBag.duration || 'month',
      slug: loadedBag.slug || null,
    }
  } catch (e) {
    return emptyBag()
  }
}

const AppComponent: FC<AppPropsWithLayout> = ({ Component, pageProps }) => {
  useCookieRedirect()
  useUtmTracking()
  const router = useRouter()

  // Each tenant has its own saved basket
  const basketKey = getTenantId(router.pathname) === Tenant.AU ? 'bag_2' : 'bag'

  const [bag, setBag] = useState<BagItem>(() => loadBag(basketKey))

  // Update basket when route changes
  useEffect(() => {
    setBag(loadBag(basketKey))
  }, [basketKey])

  // Crossing between tenants (/ <-> /au) must be a full page load: some values
  // (default planPrice, tideCashBackAmount) resolve the tenant once from the
  // URL and would keep the previous country's currency after client-side nav.
  useEffect(() => {
    const onRouteChangeStart = (url: string) => {
      if (getTenantId(url) !== getTenantId(window.location.pathname)) {
        window.location.href = url
        router.events.emit('routeChangeError')
        // Throwing is how a pages-router transition is cancelled; the full
        // page load above takes over.
        throw CROSS_TENANT_NAVIGATION_ABORT
      }
    }
    router.events.on('routeChangeStart', onRouteChangeStart)
    return () => router.events.off('routeChangeStart', onRouteChangeStart)
  }, [router.events])

  const flagsmithRef = useRef(createFlagsmithInstance())

  useEffect(() => {
    flagsmithRef.current.init({
      environmentID: Project.flagsmith,
    })
  }, [])

  // First-touch capture of gclid/msclkid/fbclid into sessionStorage. Independent
  // of GTM consent (sessionStorage is first-party + session-scoped, not a
  // tracking cookie under PECR/GDPR) so it runs even when shouldDisableTracking()
  // blocks the rest of the analytics stack. First-write wins per tab.
  // checkout-attribution is lazy-imported (here and in the effect below) to
  // keep its 21KB out of the _app chunk. Ordering (capture before ensureFbc)
  // still holds: both effects chain on the same module promise, and this
  // effect registers its callback first.
  useEffect(() => {
    import('project/checkout-attribution').then((m) =>
      m.captureClickIdsFromUrl(),
    )
  }, [])

  // Restore Meta's _fbp (browser ID) and _fbc (click ID) cookies the retired
  // Pixel (removed 2026-05-01) used to write. Both consent-gated internally.
  // ensureFbc runs after captureClickIdsFromUrl (effect above) so the fbclid is
  // already in sessionStorage. Fire on mount, then re-fire when CookieScript
  // reports a fresh acceptance so a visitor who consents after landing still
  // gets both cookies set this session.
  useEffect(() => {
    const ensure = () =>
      import('project/checkout-attribution').then((m) => {
        m.ensureFbp()
        m.ensureFbc()
      })
    ensure()
    const onConsent = () => {
      ensure()
    }
    window.addEventListener('CookieScriptAccept', onConsent)
    window.addEventListener('CookieScriptAcceptAll', onConsent)
    return () => {
      window.removeEventListener('CookieScriptAccept', onConsent)
      window.removeEventListener('CookieScriptAcceptAll', onConsent)
    }
  }, [])

  useEffect(() => {
    if (bag) {
      API.storage.setItemSync!(basketKey, JSON.stringify(bag))
    }
  }, [bag, basketKey])

  useEffect(() => {
    if (shouldDisableTracking()) {
      return
    }

    // Load GTM script — but only on production hostnames, OR when
    // explicitly testing via GTM Tag Assistant Preview Mode (which appends
    // ?gtm_debug=... to the URL).
    //
    // Vercel previews, ngrok tunnels, and localhost should NOT pollute
    // production analytics (GA4, Meta Pixel, Google Ads, Bing, LinkedIn).
    // Meta Diagnostics flagged 18 dev/preview domains as data sources on
    // 1 May 2026 — this guard prevents that recurring while still letting
    // engineers debug tag behaviour on preview deploys via Preview Mode.
    const isProductionHost = /(^|\.)hoxtonmix\.(com|com\.au)$/.test(
      window.location.hostname,
    )
    const isPreviewMode = window.location.search.includes('gtm_debug=')
    if (Project.gtm && (isProductionHost || isPreviewMode)) {
      runWhenIdle(() => {
        const gtmScript = document.createElement('script')
        gtmScript.defer = true
        gtmScript.innerHTML = `!function(){"use strict";function l(e){for(var t=e,r=0,n=document.cookie.split(";");r<n.length;r++){var o=n[r].split("=");if(o[0].trim()===t)return o[1]}}function s(e){return localStorage.getItem(e)}function u(e){return window[e]}function A(e,t){e=document.querySelector(e);return t?null==e?void 0:e.getAttribute(t):null==e?void 0:e.textContent}var e=window,t=document,r="script",n="dataLayer",o="https://analytics.hoxtonmix.com",a="",i="anbthremgh",c="5d4stk=AQNQIiYlWSleVFs%2FJU4CX1pJXEMWBkkKBx8ZChwFHRpGBAII",g="stapeUserId",v="",E="",d=!1;try{var d=!!g&&(m=navigator.userAgent,!!(m=new RegExp("Version/([0-9._]+)(.*Mobile)?.*Safari.*").exec(m)))&&16.4<=parseFloat(m[1]),f="stapeUserId"===g,I=d&&!f?function(e,t,r){void 0===t&&(t="");var n={cookie:l,localStorage:s,jsVariable:u,cssSelector:A},t=Array.isArray(t)?t:[t];if(e&&n[e])for(var o=n[e],a=0,i=t;a<i.length;a++){var c=i[a],c=r?o(c,r):o(c);if(c)return c}else console.warn("invalid uid source",e)}(g,v,E):void 0;d=d&&(!!I||f)}catch(e){console.error(e)}var m=e,g=(m[n]=m[n]||[],m[n].push({"gtm.start":(new Date).getTime(),event:"gtm.js"}),t.getElementsByTagName(r)[0]),v=I?"&bi="+encodeURIComponent(I):"",E=t.createElement(r),f=(d&&(i=8<i.length?i.replace(/([a-z]{8}$)/,"kp$1"):"kp"+i),!d&&a?a:o);E.async=!0,E.src=f+"/"+i+".js?"+c+v,null!=(e=g.parentNode)&&e.insertBefore(E,g)}();`
        document.head.appendChild(gtmScript)

        // Add GTM noscript fallback
        const noscript = document.createElement('noscript')
        noscript.innerHTML = `<iframe src="https://analytics.hoxtonmix.com/ns.html?id=GTM-K636ZW" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
        document.body.appendChild(noscript)
      })
    }

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

    if (isSafari) {
      document.body.classList.add('safari')
    }
  }, [])

  const getLayout =
    Component.getLayout || ((page) => <PageContainer>{page}</PageContainer>)

  if (router.asPath.includes('sw.js')) {
    return null
  }

  return (
    <FlagsmithProvider flagsmith={flagsmithRef.current}>
      <ShoppingBagContext.Provider
        value={{
          setValue: setBag,
          value: bag,
        }}
      >
        <LocaleContainer>
          <AnalyticsContainer Component={Component} />
          <Head>
            {!process.env.prod && (
              <meta name='robots' content='noindex, nofollow' />
            )}
            {!!Project.cookieScript && (
              <>
                {/* CookieScript is loaded + configured by the GTM web-container
                    tag "CookieScript" (#204), which fires on Consent Initialization
                    and sets the full Consent Mode v2 config (default-denied for
                    ad_storage/analytics_storage/ad_user_data/ad_personalization,
                    wait_for_update, ads_data_redaction, url_passthrough). We do NOT
                    also load it here — a second <script> from the same CDN double-
                    fetched the ~198 KB bundle and duplicated the consent init.
                    These resource hints stay to warm the connection for that GTM
                    load. (cookieScript URL retained for reference/config parity.) */}
                <link
                  rel='preconnect'
                  href='https://cdn.cookie-script.com'
                  crossOrigin='anonymous'
                />
                <link rel='dns-prefetch' href='https://cdn.cookie-script.com' />
              </>
            )}
            <meta charSet='utf-8' />
            <meta httpEquiv='x-ua-compatible' content='ie=edge' />
            <meta name='theme-color' content='#ffffff' />
            <meta
              name='viewport'
              content='width=device-width, initial-scale=1.0, maximum-scale=2.0'
            />
            <meta property='fb:admins' content='453323778023845' />
            <link
              rel='icon'
              type='image/png'
              href='/favicon-96x96.png'
              sizes='96x96'
            />
            <link rel='icon' type='image/svg+xml' href='/favicon.svg' />
            <link rel='shortcut icon' href='/favicon.ico' />
            <link
              rel='apple-touch-icon'
              sizes='180x180'
              href='/apple-touch-icon.png'
            />

            <link rel='manifest' href='/site.webmanifest' />
            <meta name='theme-color' content='#ffffff' />
            <link rel='manifest' href='/manifest.webmanifest' />
            <link
              rel='preload'
              as='font'
              type='font/woff2'
              href='/fonts/open-sans-v17-latin-regular.woff2'
              crossOrigin={'crossorigin'}
            />
            <link
              rel='preload'
              as='font'
              type='font/woff2'
              href='/fonts/open-sans-v17-latin-600.woff2'
              crossOrigin={'crossorigin'}
            />
            <link
              rel='preload'
              as='font'
              type='font/woff2'
              href='/fonts/open-sans-v17-latin-700.woff2'
              crossOrigin={'crossorigin'}
            />
            <link rel='dns-prefetch' href='https://images.prismic.io' />
            {/* Preconnect (no crossOrigin): hero/LCP images load via plain
                <img>, which uses non-CORS connections — a crossOrigin
                preconnect would warm a connection the image fetch can't
                reuse. dns-prefetch kept as a fallback for older browsers. */}
            <link rel='preconnect' href='https://imgproxy.hoxtonmix.com' />
            <link rel='dns-prefetch' href='https://imgproxy.hoxtonmix.com' />
            <link rel='dns-prefetch' href='https://analytics.hoxtonmix.com' />
          </Head>

          <ToltCouponProvider />
          <NProgress />
          {getLayout(
            // @ts-ignore
            <Component {...pageProps} />,
            pageProps,
          )}
        </LocaleContainer>

        <ScrollOnRoute />
      </ShoppingBagContext.Provider>
    </FlagsmithProvider>
  )
}

export default AppComponent
